import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
} from '@mui/material';

import tmsBackground from '../assets/tms/tms-background.webp';
const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';
 
const benefits = [
  'Non-invasive and drug-free',
  'FDA-cleared treatment option',
  'Minimal side effects for most patients',
  'No downtime after sessions',
  'Personalized treatment planning',
  'Helpful for treatment-resistant depression',
];

const pathway = [
  {
    title: 'Evaluation',
    description:
      'Your provider reviews your symptoms, medical history, and past treatment responses to determine whether TMS may be appropriate.',
  },
  {
    title: 'Treatment Sessions',
    description:
      'TMS sessions are completed in-office using focused magnetic pulses while you remain awake and comfortable.',
  },
  {
    title: 'Ongoing Monitoring',
    description:
      'Our team monitors your progress throughout treatment and adjusts care recommendations when needed.',
  },
];

const infoCards = [
  {
    title: 'What is TMS Therapy?',
    description:
      'Transcranial Magnetic Stimulation, or TMS, is a non-invasive treatment that uses magnetic pulses to stimulate areas of the brain involved in mood regulation. It may be helpful for patients who have not found enough relief from medication or therapy alone.',
  },
  {
    title: 'What to Expect During Treatment',
    description:
      'TMS is performed in an outpatient setting and does not require anesthesia or sedation. Sessions typically last about 20 to 40 minutes, and most patients can return to normal daily activities immediately afterward.',
  },
];

export default function TMS() {
  return (
    <>
      <Helmet>
        <title>TMS Therapy for Depression | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Aspire Behavioral Health offers non-invasive TMS therapy for depression for patients who have not found enough relief from traditional treatment options."
        />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',

          minHeight: {
            xs: 520,
            sm: 580,
            md: 650,
            lg: 620,
          },

          py: {
            xs: 8,
            sm: 10,
            md: 12,
            lg: 10,
          },

          overflow: 'hidden',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: 'white',
        }}
      >
        {/* Background Image */}
        <Box
          component="img"
          src={tmsBackground}
          alt="TMS therapy at Aspire Behavioral Health"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            zIndex: -2,
          }}
        />

        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: -1,
          }}
        />

        {/* Hero Content */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 3,
            maxWidth: '850px',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: '2.2rem', sm: '3.5rem' },
              lineHeight: 1.2,
            }}
          >
            TMS Therapy for Depression
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 300,
              lineHeight: 1.8,
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.2rem' },
            }}
          >
            A non-invasive, drug-free treatment option for patients who have not
            found enough relief from traditional depression treatments.
          </Typography>

          {/* Hero Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                          component="a"
                          href={zocdocLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          sx={{
                            borderColor: 'white',
                            color: 'white !important',
                            fontWeight: 600,
                            px: 4,
                            py: 1.3,
                            borderRadius: '30px',
                            textTransform: 'none',
                            '&:visited': {
                              color: 'white',
                            },
                            '&:hover': {
                              borderColor: 'white',
                              backgroundColor: 'rgba(255,255,255,0.1)',
                              color: 'white',
                            },
                          }}
                        >
                          Schedule an Appointment
                        </Button>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ backgroundColor: 'white', py: 8, px: 2 }}>
        {/* Intro */}
        <Box sx={{ textAlign: 'center', maxWidth: '900px', mx: 'auto', mb: 7 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
            Our Approach to TMS
          </Typography>

          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.9,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            At Aspire Behavioral Health, we believe effective treatment begins
            with understanding your unique needs. Our TMS therapy process starts
            with a clinical evaluation where we review your medical history,
            symptoms, and past treatment responses.
            <br />
            <br />
            TMS uses focused magnetic pulses to stimulate areas of the brain
            involved in mood regulation. Treatment is non-invasive, does not
            require sedation, and is completed in a comfortable outpatient
            setting with supervision from trained staff.
            <br />
            <br />
            Treatment plans are personalized based on your needs and progress.
            Our team continues to monitor your response and support you
            throughout the process.
            <br />
            <br />
            To learn more or find out if TMS may be right for you, call{' '}
            <a href="tel:2108669850">210-866-9850</a>.
          </Typography>
        </Box>

        <Divider
          sx={{
            width: { xs: '80%', sm: '60%', md: '40%' },
            mx: 'auto',
            mb: 7,
            borderColor: '#d9d9d9',
          }}
        />

        {/* Benefits */}
        <Box
          sx={{
            backgroundColor: '#e3f3c5',
            color: 'black',
            maxWidth: '1200px',
            mx: 'auto',
            p: { xs: 3, sm: 5 },
            borderRadius: 4,
            boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{ textAlign: 'center', fontWeight: 600, mb: 2 }}
          >
            Benefits of TMS Therapy
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            TMS may be a helpful option for individuals who continue to struggle
            with depression symptoms despite trying other treatments.
          </Typography>

          <Grid container spacing={2} justifyContent="center" alignItems="stretch">
            {benefits.map((item) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                key={item}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Card
                  sx={{
                    width: '100%',
                    maxWidth: 340,
                    borderRadius: 3,
                    boxShadow: 'none',
                    backgroundColor: 'rgba(255,255,255,0.75)',
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {item}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Info Cards */}
        <Box sx={{ maxWidth: '1000px', mx: 'auto', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            About Treatment
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            TMS is designed to be a comfortable outpatient treatment option with
            no anesthesia, no sedation, and no required recovery time.
          </Typography>

          {infoCards.map((item) => (
            <Card
              key={item.title}
              sx={{
                borderRadius: 4,
                boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                mb: 4,
              }}
            >
              <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                  {item.title}
                </Typography>

                <Typography sx={{ lineHeight: 1.9, color: 'text.secondary' }}>
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Care Pathway */}
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            mb: 8,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              mb: 2,
            }}
          >
            The TMS Care Pathway
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              maxWidth: '750px',
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              color: 'text.secondary',
            }}
          >
            Our team guides you from evaluation through treatment and follow-up
            support.
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {pathway.map((step, index) => (
              <Grid item xs={12} sm={6} md={4} key={step.title}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: '#3a4c17',
                      }}
                    >
                      Step {index + 1}
                    </Typography>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      {step.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.8, color: 'text.secondary' }}
                    >
                      {step.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
            Ready to learn more about TMS?
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
                            component="a"
                            href={zocdocLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            sx={{
                              backgroundColor: 'transparent',
                              border: '2px solid #3a4c17',
                              color: '#3a4c17 !important',
                              fontWeight: 600,
                              px: 4,
                              py: 1.3,
                              borderRadius: '30px',
                              textTransform: 'none',
                              '&:visited': {
                                color: '#3a4c17',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(58,76,23,0.05)',
                                border: '2px solid #3a4c17',
                                color: '#3a4c17',
                              },
                            }}
                          >
                            Schedule an Appointment
                          </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}